import { useEffect, useState } from 'react';
import type { EmailSettings, EmailTemplateKey, EmailTemplateResponse, NotificationEventKey, NotificationSettings } from '@orion/contracts';
import { NOTIFICATION_EVENT_KEYS } from '@orion/contracts';
import { Button, Dialog, Switch, Textarea } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { ApiError } from '../../../lib/api-client.js';
import { useToast } from '../../../lib/toast.js';
import { useEmailTemplatesQuery, useResetEmailTemplate, useUpdateEmailTemplate } from '../email-templates/api.js';
import { useSendTestEmail, useSettingsQuery, useUpdateSettings } from './api.js';

const PROVIDERS: Array<{ value: EmailSettings['provider']; label: string; sub: string; icon: 'ms' | 'mail' | 'smtp' | 'bolt' }> = [
  { value: 'brevo', label: 'Brevo', sub: 'Transactional email API', icon: 'bolt' },
  { value: 'smtp', label: 'SMTP', sub: 'Any relay · host, port, TLS (e.g. Brevo SMTP)', icon: 'smtp' },
  { value: 'msgraph', label: 'Microsoft 365', sub: 'Connected via Graph API', icon: 'ms' },
  { value: 'gmail', label: 'Gmail / Google Workspace', sub: 'OAuth · Gmail API', icon: 'mail' },
];

const NOTIFICATION_EVENT_LABEL: Record<NotificationEventKey, string> = {
  leadCreated: 'New lead created (to admins)',
  leadAssigned: 'Lead assigned to me',
  mention: '@mention',
  taskReminder: 'Task reminder',
  dealWonLost: 'Deal won / lost',
  importCompleted: 'Import finished',
  teamMemberAdded: 'Added to a team',
  teamMemberRemoved: 'Removed from a team',
};

function ProviderIcon({ icon }: { icon: 'ms' | 'mail' | 'smtp' | 'bolt' }) {
  if (icon === 'ms') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
        <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
        <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
        <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
      </svg>
    );
  }
  if (icon === 'mail') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </svg>
    );
  }
  if (icon === 'smtp') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="7" rx="1.5" />
        <rect x="3" y="13" width="18" height="7" rx="1.5" />
        <path d="M7 7.5h.01M7 16.5h.01" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 3 10 14" />
      <path d="M21 3 14 21l-4-7-7-4z" />
    </svg>
  );
}

function TinySwitch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex h-4.5 w-8 items-center rounded-full p-0.5 disabled:opacity-50"
      style={{ background: checked ? 'var(--color-primary)' : 'var(--color-border-strong)', justifyContent: checked ? 'flex-end' : 'flex-start' }}
    >
      <span className="size-3.5 rounded-full bg-white" />
    </button>
  );
}

function TemplateEditor({ template, onClose }: { template: EmailTemplateResponse; onClose: () => void }) {
  const update = useUpdateEmailTemplate();
  const reset = useResetEmailTemplate();
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.bodyHtml);

  return (
    <Dialog
      open
      title={`Edit template: ${template.label}`}
      onClose={onClose}
      actions={
        <>
          {template.isCustom && (
            <Button
              variant="secondary"
              size="sm"
              loading={reset.isPending}
              onClick={async () => {
                await reset.mutateAsync(template.key as EmailTemplateKey);
                onClose();
              }}
            >
              Reset to default
            </Button>
          )}
          <Button
            size="sm"
            loading={update.isPending}
            onClick={async () => {
              await update.mutateAsync({ key: template.key as EmailTemplateKey, input: { subject, bodyHtml } });
              onClose();
            }}
          >
            Save template
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
          Subject
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" />
        </label>
        <Textarea label="Body (HTML)" rows={6} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} className="font-mono text-xs" />
        <p className="text-xs text-text-muted">Placeholders: {template.placeholders.join(', ')}</p>
        <p className="text-xs text-text-muted">
          This is the message only — your logo, brand color, and (where this email has one) its button are added automatically. Write plain paragraphs here, not a full page.
        </p>
      </div>
    </Dialog>
  );
}

/** docs section 5 "Administrator Control" — Email & notifications
 * (design/orion-crm-design/rendered-html/AdminEmail.html). Combines two
 * settings namespaces (`email`, `notifications`) plus the email-template
 * catalogue on one page, matching the single design frame — `/admin/
 * notifications` now redirects here rather than keeping a second editor for
 * the same `notifications` namespace. */
export function EmailSettingsPage() {
  const { data: email, isLoading: loadingEmail } = useSettingsQuery<EmailSettings>('email');
  const { data: notifications, isLoading: loadingNotif } = useSettingsQuery<NotificationSettings>('notifications');
  const { data: templates } = useEmailTemplatesQuery();
  const updateEmail = useUpdateSettings<EmailSettings>('email');
  const updateNotifications = useUpdateSettings<NotificationSettings>('notifications');
  const sendTest = useSendTestEmail();
  const toast = useToast();

  const [emailDraft, setEmailDraft] = useState<EmailSettings | null>(null);
  const [notifDraft, setNotifDraft] = useState<NotificationSettings | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplateResponse | null>(null);
  const [testResult, setTestResult] = useState<'idle' | 'sent' | 'error'>('idle');

  useEffect(() => {
    if (email && emailDraft === null) setEmailDraft(email);
  }, [email, emailDraft]);
  useEffect(() => {
    if (notifications && notifDraft === null) setNotifDraft(notifications);
  }, [notifications, notifDraft]);

  const dirty = (email && emailDraft && JSON.stringify(email) !== JSON.stringify(emailDraft)) || (notifications && notifDraft && JSON.stringify(notifications) !== JSON.stringify(notifDraft));

  const discard = () => {
    setEmailDraft(email ?? null);
    setNotifDraft(notifications ?? null);
  };
  const save = async () => {
    if (emailDraft) await updateEmail.mutateAsync(emailDraft);
    if (notifDraft) await updateNotifications.mutateAsync(notifDraft);
  };

  const setEventRule = (key: NotificationEventKey, patch: Partial<NotificationSettings[NotificationEventKey]>) => {
    setNotifDraft((d) => (d ? { ...d, [key]: { ...d[key], ...patch } } : d));
  };

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Email & notifications' }];

  if (loadingEmail || loadingNotif || !emailDraft || !notifDraft) return <PageHeader breadcrumb={breadcrumb} />;

  const configured = Boolean(emailDraft.fromName && emailDraft.fromAddress);

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="flex flex-col gap-5 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Email &amp; notifications</h1>
            <p className="text-sm leading-5 text-text-muted">Choose how Orion sends email, manage templates and decide who is notified about what.</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="secondary" size="md" onClick={discard} disabled={!dirty}>
              Discard
            </Button>
            <Button size="md" onClick={save} loading={updateEmail.isPending || updateNotifications.isPending} disabled={!dirty}>
              Save changes
            </Button>
          </div>
        </div>

        <div className="grid flex-1 grid-cols-[500px_1fr] items-start gap-4 overflow-y-auto">
          <div className="flex flex-col gap-4">
            <section aria-labelledby="prov-h" className="flex flex-col gap-3.5 rounded-dialog border border-border bg-surface p-5 shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <h2 id="prov-h" className="text-[15px] font-semibold leading-5">
                    Sending provider
                  </h2>
                  <span className="text-[13px] text-text-muted">Used for invites, password resets and notification email.</span>
                </div>
                <span className={`flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold ${configured ? 'bg-success-soft text-success' : 'bg-amber-soft text-amber-text'}`}>
                  <span className="size-1.5 rounded-full" style={{ background: configured ? 'var(--color-success)' : 'var(--color-amber-deep)' }} />
                  {configured ? 'Configured' : 'Incomplete'}
                </span>
              </div>

              <div role="radiogroup" aria-label="Sending provider" className="grid grid-cols-2 gap-2">
                {PROVIDERS.map((p) => {
                  const checked = emailDraft.provider === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      onClick={() => setEmailDraft((d) => (d ? { ...d, provider: p.value } : d))}
                      className={`flex h-15 items-center gap-2.5 rounded-input border px-3 ${checked ? 'border-primary bg-primary-soft' : 'border-border bg-surface'}`}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-control border border-border bg-surface">
                        <ProviderIcon icon={p.icon} />
                      </span>
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block truncate text-sm font-semibold">{p.label}</span>
                        <span className="block truncate text-xs" style={{ color: checked ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                          {p.sub}
                        </span>
                      </span>
                      <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]" style={{ borderColor: checked ? 'var(--color-primary)' : 'var(--color-border-strong)', borderWidth: checked ? 5 : 1.5 }} />
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 text-xs text-text-muted">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                Credentials are stored in Doppler, never in the database.
              </div>

              <div className="border-t border-divider pt-3.5">
                <div className="grid grid-cols-2 items-end gap-3">
                  <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                    From name
                    <input value={emailDraft.fromName} onChange={(e) => setEmailDraft((d) => (d ? { ...d, fromName: e.target.value } : d))} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" />
                  </label>
                  <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                    From address
                    <input type="email" value={emailDraft.fromAddress} onChange={(e) => setEmailDraft((d) => (d ? { ...d, fromAddress: e.target.value } : d))} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" />
                  </label>
                  <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                    Reply-to
                    <input type="email" value={emailDraft.replyTo ?? ''} onChange={(e) => setEmailDraft((d) => (d ? { ...d, replyTo: e.target.value } : d))} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" />
                  </label>
                  <div className="flex items-center gap-2.5">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={sendTest.isPending}
                      onClick={async () => {
                        try {
                          await sendTest.mutateAsync();
                          setTestResult('sent');
                          toast.success('Test email sent — check your inbox.');
                        } catch (err) {
                          setTestResult('error');
                          const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to send test email.';
                          toast.error(detail);
                        }
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 3 10 14" />
                        <path d="M21 3 14 21l-4-7-7-4z" />
                      </svg>
                      Send test email
                    </Button>
                    <span className="text-xs leading-4 text-text-muted">
                      {testResult === 'sent' ? 'Sent!' : testResult === 'error' ? 'Failed to send.' : 'To your own address'}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <section aria-labelledby="tpl-h" className="overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
              <div className="flex h-14 items-center justify-between px-5">
                <div className="flex items-baseline gap-2">
                  <h2 id="tpl-h" className="text-[15px] font-semibold">
                    Email templates
                  </h2>
                  <span className="text-[13px] text-text-muted">{templates?.length ?? 0} templates</span>
                </div>
              </div>
              <div className="grid h-8 items-center px-5 text-xs font-semibold text-text-muted" style={{ gridTemplateColumns: '1.7fr 1fr 1.5fr 52px', background: 'var(--color-surface-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
                <span>Template</span>
                <span>Type</span>
                <span>Last updated</span>
                <span />
              </div>
              {(templates ?? []).map((t) => (
                <div key={t.key} className="grid h-10 items-center border-b border-divider px-5 text-sm last:border-b-0" style={{ gridTemplateColumns: '1.7fr 1fr 1.5fr 52px' }}>
                  <span className="font-semibold">{t.label}</span>
                  <span>
                    <span className={`inline-flex h-5.5 items-center gap-1 rounded-chip px-2 text-xs font-medium ${t.isCustom ? 'bg-primary-soft text-primary-soft-text font-semibold' : 'border border-border text-text-secondary'}`}>
                      {!t.isCustom && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="4" y="11" width="16" height="10" rx="2" />
                          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                        </svg>
                      )}
                      {t.isCustom ? 'Custom' : 'System'}
                    </span>
                  </span>
                  <span className="text-[13px] text-text-muted">{t.updatedAt ? `${new Date(t.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${t.updatedByName}` : '—'}</span>
                  <button type="button" onClick={() => setEditingTemplate(t)} className="text-right text-[13px] font-semibold text-primary">
                    Edit
                  </button>
                </div>
              ))}
            </section>
          </div>

          <div className="flex flex-col gap-4">
            <section aria-labelledby="notif-h" className="overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
              <div className="flex flex-col gap-0.5 px-5 pb-3.5 pt-4.5">
                <h2 id="notif-h" className="text-[15px] font-semibold">
                  Notification rules
                </h2>
                <span className="text-[13px] text-text-muted">Workspace defaults for every user.</span>
              </div>
              <div className="grid h-10 items-center px-5 text-xs font-semibold leading-3.5 text-text-muted" style={{ gridTemplateColumns: '1fr 56px 56px 72px', background: 'var(--color-surface-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
                <span>Event</span>
                <span className="text-center">In-app</span>
                <span className="text-center">Email</span>
                <span className="text-center">Users can opt out</span>
              </div>
              {NOTIFICATION_EVENT_KEYS.map((key) => (
                <div key={key} className="grid h-10 items-center border-b border-divider px-5 text-[13px] last:border-b-0" style={{ gridTemplateColumns: '1fr 56px 56px 72px' }}>
                  <span className="font-medium">{NOTIFICATION_EVENT_LABEL[key]}</span>
                  <span className="flex justify-center">
                    <TinySwitch checked={notifDraft[key].inApp} onChange={(v) => setEventRule(key, { inApp: v })} label={`${NOTIFICATION_EVENT_LABEL[key]}, in-app`} />
                  </span>
                  <span className="flex justify-center">
                    <TinySwitch checked={notifDraft[key].email} onChange={(v) => setEventRule(key, { email: v })} label={`${NOTIFICATION_EVENT_LABEL[key]}, email`} />
                  </span>
                  <span className="flex justify-center">
                    <TinySwitch checked={notifDraft[key].optOutAllowed} onChange={(v) => setEventRule(key, { optOutAllowed: v })} label={`${NOTIFICATION_EVENT_LABEL[key]}, users can opt out`} />
                  </span>
                </div>
              ))}
            </section>

            <section aria-labelledby="track-h" className="flex flex-col gap-3 rounded-dialog border border-border bg-surface p-5 shadow-card">
              <h2 id="track-h" className="text-[15px] font-semibold">
                Tracking
              </h2>
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold">Log sent emails to timeline</span>
                  <span className="text-xs leading-4 text-text-muted">Emails sent from Orion appear on the lead, contact and deal.</span>
                </div>
                <Switch checked={notifDraft.logSentEmailsToTimeline} onChange={(v) => setNotifDraft((d) => (d ? { ...d, logSentEmailsToTimeline: v } : d))} label="Log sent emails to timeline" hideLabel />
              </div>
              <div className="border-t border-divider" />
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold">Open tracking</span>
                  <span className="text-xs leading-4 text-text-muted">Adds a 1×1 tracking pixel to outgoing email.</span>
                </div>
                <Switch checked={notifDraft.openTracking} onChange={(v) => setNotifDraft((d) => (d ? { ...d, openTracking: v } : d))} label="Open tracking" hideLabel />
              </div>
              <div className="flex items-start gap-2 rounded-control border border-divider bg-surface-subtle p-2.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0 text-text-secondary">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5M12 8h.01" />
                </svg>
                <span className="text-xs leading-4 text-text-secondary">Off by default for privacy. Many mail clients block pixels, and some regions require recipient consent before open tracking.</span>
              </div>
            </section>
          </div>
        </div>
      </main>
      {editingTemplate && <TemplateEditor template={editingTemplate} onClose={() => setEditingTemplate(null)} />}
    </>
  );
}
